import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "../../../components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"

export interface ResponsesCompactionConfig {
  trigger_tokens: number
  min_messages: number
}

interface EditResponsesCompactionDialogProps {
  isOpen: boolean
  config: ResponsesCompactionConfig
  onSave: (config: ResponsesCompactionConfig) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function EditResponsesCompactionDialog({
  isOpen,
  config,
  onSave,
  onOpenChange,
}: EditResponsesCompactionDialogProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [triggerTokens, setTriggerTokens] = useState(45000)
  const [minMessages, setMinMessages] = useState(20)

  useEffect(() => {
    setTriggerTokens(config.trigger_tokens)
    setMinMessages(config.min_messages)
  }, [config])

  const handleSubmit = async () => {
    if (triggerTokens < 1) {
      toast.error(
        t("adminDashboard.features.responsesCompaction.triggerError", {
          defaultValue: "Trigger tokens must be at least 1",
        })
      )
      return
    }
    if (minMessages < 1) {
      toast.error(
        t("adminDashboard.features.responsesCompaction.minMessagesError", {
          defaultValue: "Minimum messages must be at least 1",
        })
      )
      return
    }

    setSaving(true)
    try {
      await onSave({
        trigger_tokens: triggerTokens,
        min_messages: minMessages,
      })
      toast.success(
        t("adminDashboard.features.responsesCompaction.success", {
          defaultValue: "Configuration updated successfully",
        })
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        t("adminDashboard.features.responsesCompaction.error", {
          defaultValue: "Failed to update configuration",
        }) + (error instanceof Error ? `: ${error.message}` : "")
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.features.responsesCompaction.configTitle", {
              defaultValue: "Responses Compaction Configuration",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.features.responsesCompaction.configDescription", {
              defaultValue:
                "Tune when context compaction runs before the Responses tool loop. Feature on/off is controlled by the row toggle.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="compaction-trigger-tokens">
              {t("adminDashboard.features.responsesCompaction.triggerTokens", {
                defaultValue: "Trigger tokens",
              })}
            </Label>
            <Input
              id="compaction-trigger-tokens"
              min="1"
              type="number"
              value={triggerTokens}
              onChange={(e) => setTriggerTokens(parseInt(e.target.value, 10) || 1)}
            />
            <p className="text-muted-foreground text-sm">
              {t("adminDashboard.features.responsesCompaction.triggerTokensHelp", {
                defaultValue:
                  "Compaction is considered when estimated history tokens reach this value.",
              })}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="compaction-min-messages">
              {t("adminDashboard.features.responsesCompaction.minMessages", {
                defaultValue: "Minimum history messages",
              })}
            </Label>
            <Input
              id="compaction-min-messages"
              min="1"
              type="number"
              value={minMessages}
              onChange={(e) => setMinMessages(parseInt(e.target.value, 10) || 1)}
            />
            <p className="text-muted-foreground text-sm">
              {t("adminDashboard.features.responsesCompaction.minMessagesHelp", {
                defaultValue:
                  "Compaction requires at least this many history messages (excluding the latest user turn).",
              })}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button disabled={saving} variant="outline" onClick={() => onOpenChange(false)}>
            {t("adminDashboard.features.responsesCompaction.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled={saving} onClick={handleSubmit}>
            {saving
              ? t("adminDashboard.features.responsesCompaction.saving", {
                  defaultValue: "Saving...",
                })
              : t("adminDashboard.features.responsesCompaction.save", {
                  defaultValue: "Save Configuration",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
